import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, generatePath } from "react-router-dom";
import { ChevronRight, ChevronDown, Menu, X, Folder, MessageSquare, MoreHorizontal, Edit2, Trash2, Plus, Settings } from 'lucide-react';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function calendarDayDiff(now: Date, date: Date) {
  // Use UTC midnights to avoid DST issues
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor((a - b) / MS_PER_DAY);
}

interface SidebarProps {
  projects: Project[];
  isExpanded: boolean;
  onToggle: () => void;
  onThreadSelect?: (projectId: string, threadId: string) => void;
  selectedProjectId: string | null;
  selectedThreadId: string | null;
  onThreadEdit: (projectId: string, threadId: string, newTitle: string) => void;
  onThreadDelete: (projectId: string, threadId: string) => void;
  onThreadCreate: (projectId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  projects, 
  isExpanded, 
  onToggle, 
  onThreadSelect, 
  selectedProjectId, 
  selectedThreadId, 
  onThreadEdit, 
  onThreadDelete,
  onThreadCreate,
}) => {
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingThread, setEditingThread] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const goToSettings = (projectId: string) => {
    navigate(
      generatePath("/projects/:project_id/settings", {
        project_id: projectId
      }),
      { replace: true }
    );
  }

  const toggleProject = (projectId: string) => {
    const newExpandedProjects = new Set(expandedProjects);
    if (newExpandedProjects.has(projectId)) {
      newExpandedProjects.delete(projectId);
    } else {
      newExpandedProjects.add(projectId);
    }
    setExpandedProjects(newExpandedProjects);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close sidebar when clicking outside (only on desktop when expanded)
  useEffect(() => {
    const handleSidebarClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('[data-sidebar="true"]');
      const target = event.target as Node;
      
      if (isExpanded && sidebar && !sidebar.contains(target)) {
        // Don't close if clicking on mobile overlay (it has its own handler)
        if (!(target as Element)?.classList?.contains('fixed')) {
          onToggle();
        }
      }
    };

    // Only add listener on desktop when expanded
    if (isExpanded && window.innerWidth >= 1024) {
      document.addEventListener('mousedown', handleSidebarClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleSidebarClickOutside);
    };
  }, [isExpanded, onToggle]);

  const handleEditClick = (threadId: string, currentTitle: string) => {
    setEditingThread(threadId);
    setEditTitle(currentTitle);
    setActiveDropdown(null);
  };

  const handleEditSubmit = (projectId: string, threadId: string) => {
    if (editTitle.trim() && onThreadEdit) {
      onThreadEdit(projectId, threadId, editTitle.trim());
    }
    setEditingThread(null);
    setEditTitle('');
  };

  const handleEditCancel = () => {
    setEditingThread(null);
    setEditTitle('');
  };

  const handleDeleteClick = (projectId: string, threadId: string) => {
    if (onThreadDelete) {
      onThreadDelete(projectId, threadId);
    }
    setActiveDropdown(null);
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const days = calendarDayDiff(now, date);
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div 
        data-sidebar="true"
        className={`
          fixed left-0 top-0 h-full bg-gray-900 border-r border-gray-700/50 z-50 transition-all duration-300 ease-in-out flex flex-col
          ${isExpanded ? 'w-80' : 'w-12'}
        `}
      >
        {/* Header */}
        <div className={`flex items-center border-b border-gray-700/50 p-3 flex-shrink-0 ${isExpanded ? 'justify-between' : 'justify-center'}`}>
          <button
            onClick={onToggle}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? (
              <X className="w-5 h-5 text-gray-300" />
            ) : (
              <Menu className="w-5 h-5 text-gray-300" />
            )}
          </button>
          
          {isExpanded && (
            <h2 className="text-lg font-semibold text-gray-100">Projects</h2>
          )}
        </div>

        {/* Content */}
        <div className={`flex-1 min-h-0 ${isExpanded ? 'block' : 'hidden'}`}>
          <div className="h-full overflow-y-auto p-3">
            {projects.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No projects yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((project) => (
                  <div key={project.project_id} className="border border-gray-700/50 rounded-lg bg-gray-800/60">
                    {/* Project Header */}
                    <div className="group relative">
                      <button
                        onClick={() => toggleProject(project.project_id)}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <Folder className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-gray-200 truncate">
                            {project.project_name}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-400">
                            {project.threads.length}
                          </span>
                          {expandedProjects.has(project.project_id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>
                      
                      {/* Create Thread Button - appears on hover */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onThreadCreate(project.project_id);
                        }}
                        className="absolute right-12 top-3 opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-500/20 rounded transition-all duration-200 bg-gray-800 border border-gray-700"
                        title="Create new thread"
                      >
                        <Plus className="w-4 h-4 text-blue-400" />
                      </button>
                      {/* Projects settings - appears on hover */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToSettings(project.project_id);
                        }}
                        className="absolute right-20 top-3 opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-500/20 rounded transition-all duration-200 bg-gray-800 border border-gray-700"
                        title="Edit project settings"
                      >
                        <Settings className="w-4 h-4 text-blue-400" />
                      </button>
                    </div>

                    {/* Threads List */}
                    {expandedProjects.has(project.project_id) && (
                      <div className="border-t border-gray-700/50">
                        {project.threads.length === 0 ? (
                          <div className="p-3 text-center text-gray-400 text-sm">
                            No threads yet
                          </div>
                        ) : (
                          <div className="space-y-1 p-2">
                            {project.threads.map((thread) => {
                              const isSelected = selectedProjectId === project.project_id && selectedThreadId === thread.thread_id;
                              const isEditing = editingThread === thread.thread_id;
                              
                              return (
                                <div
                                  key={thread.thread_id}
                                  className={`relative group rounded-md ${
                                    isSelected ? 'bg-blue-500/20 border border-blue-500/50' : 'hover:bg-gray-700/50'
                                  }`}
                                >
                                  {isEditing ? (
                                    <div className="flex items-center space-x-2 p-2">
                                      <MessageSquare className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                      <input
                                        type="text"
                                        value={editTitle}
                                        onChange={(e) => setEditTitle(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleEditSubmit(project.project_id, thread.thread_id);
                                          } else if (e.key === 'Escape') {
                                            handleEditCancel();
                                          }
                                        }}
                                        onBlur={() => handleEditSubmit(project.project_id, thread.thread_id)}
                                        className="flex-1 text-sm px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-900 text-gray-200"
                                        autoFocus
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex items-start justify-between">
                                      <button
                                        onClick={() => onThreadSelect?.(project.project_id, thread.thread_id)}
                                        className="flex-1 flex items-start space-x-2 p-2 transition-colors text-left"
                                      >
                                        {/* Thumbnail */}
                                        <div className="relative w-12 h-12 rounded overflow-hidden bg-gray-950 border border-gray-700 shrink-0">
                                          <img
                                            src={thread.thumbnail_url || '/placeholder-thumb.png'}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-contain"  /* use object-cover if you prefer cropped fill */
                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/placeholder-thumb.png'; }}
                                            draggable={false}
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm truncate ${
                                            isSelected 
                                              ? 'text-blue-300 font-medium' 
                                              : 'text-gray-300 group-hover:text-gray-100'
                                          }`}>
                                            {thread.name}
                                          </p>
                                          <p className={`text-xs mt-1 ${
                                            isSelected ? 'text-blue-400' : 'text-gray-500'
                                          }`}>
                                            {formatDate(new Date(thread.updated_at))}
                                          </p>
                                        </div>
                                      </button>
                                      
                                      {/* Three dots menu */}
                                      <div className="relative" ref={activeDropdown === thread.thread_id ? dropdownRef : null}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveDropdown(activeDropdown === thread.thread_id ? null : thread.thread_id);
                                          }}
                                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-600 rounded transition-all duration-150 m-1"
                                        >
                                          <MoreHorizontal className="w-4 h-4 text-gray-400" />
                                        </button>
                                        
                                        {/* Dropdown menu */}
                                        {activeDropdown === thread.thread_id && (
                                          <div className="absolute right-0 top-8 bg-gray-800 border border-gray-700 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
                                            <button
                                              onClick={() => handleEditClick(thread.thread_id, thread.name)}
                                              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                              <span>Edit Name</span>
                                            </button>
                                            <button
                                              onClick={() => handleDeleteClick(project.project_id, thread.thread_id)}
                                              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/20 transition-colors"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                              <span>Delete Thread</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default Sidebar;
